import { products } from "../data/products.js";
import { Hero } from "../components/marketing/Hero.js";
import { SectionHeader } from "../components/common/SectionHeader.js";
import { ProductCard } from "../components/products/ProductCard.js";
import { EcosystemDiagram } from "../components/marketing/EcosystemDiagram.js";
import { ProcessSteps } from "../components/marketing/ProcessSteps.js";
import { ContactForm } from "../components/marketing/ContactForm.js";
import { MotionStrip } from "../components/marketing/MotionStrip.js";

const needs = [
  ["01", "Gestión de proyectos y obras", "Presupuestos, avances, costos, incidencias y documentos bajo control."],
  ["02", "Administración empresarial", "Facturación, compras, gastos y operación financiera centralizada."],
  ["03", "Facturación electrónica", "Emisión y seguimiento de e-CF conectados con sistemas administrativos."],
  ["04", "Automatización de procesos", "Menos tareas repetitivas mediante flujos y reglas de negocio."],
  ["05", "Captura inteligente de gastos", "OCR y mensajería para convertir comprobantes en datos procesables."],
  ["06", "Integración entre sistemas", "Información que fluye entre productos propios y plataformas existentes."],
];

const reasons = [
  ["Mercado local", "Productos adaptados a procesos y requerimientos de República Dominicana."],
  ["Integración", "Conectamos las soluciones con sistemas que tu empresa ya utiliza."],
  ["Acompañamiento", "Implementación, capacitación y soporte durante la adopción."],
  ["Escalabilidad", "Infraestructura y arquitectura preparadas para evolucionar."],
  ["Automatización", "Convertimos tareas manuales en flujos consistentes y medibles."],
  ["Ecosistema", "Diseñamos soluciones conectadas, no herramientas aisladas."],
];

const services = [
  ["Implementación de software", "Configuración, adaptación y puesta en marcha de productos."],
  ["Integraciones", "Conexión de datos y procesos entre plataformas."],
  ["Automatización", "Flujos para reducir digitación y tareas repetitivas."],
  ["Desarrollo a medida", "Módulos y soluciones cuando el proceso lo requiere."],
  ["Consultoría tecnológica", "Diagnóstico y definición de una ruta técnica viable."],
  ["Soporte y mantenimiento", "Continuidad operativa, mejoras y evolución."],
];

export function HomePage() {
  return `
    <main id="contenido">
      ${Hero()}
      ${MotionStrip()}

      <section id="productos" class="section-pad bg-white scroll-mt-24" aria-labelledby="products-title">
        <div class="site-container">
          <div class="section-heading-row">
            ${SectionHeader({ eyebrow: "Productos", title: "Un ecosistema para operar con más control.", description: "Cada producto resuelve un proceso específico y está preparado para conectarse con el resto de tu operación." })}
            <span class="section-index">01 — 0${products.length}</span>
          </div>
          <div class="products-grid">${products.map(ProductCard).join("")}</div>
        </div>
      </section>

      <section class="section-pad ecosystem-section" aria-labelledby="ecosystem-title">
        <div class="site-container">
          ${SectionHeader({ eyebrow: "Ecosistema conectado", title: "Cuatro productos. Una visión tecnológica compartida.", description: "La información puede avanzar entre captura, proyectos, administración y facturación sin convertir cada sistema en una isla.", align: "center" })}
          ${EcosystemDiagram()}
        </div>
      </section>

      <section id="soluciones" class="section-pad bg-white scroll-mt-24" aria-labelledby="solutions-title">
        <div class="site-container solutions-layout">
          <div class="solutions-sticky">
            ${SectionHeader({ eyebrow: "Soluciones por necesidad", title: "Comenzamos por el problema operativo.", description: "No todas las empresas necesitan lo mismo. Identificamos el punto de fricción y conectamos la combinación adecuada de producto, implementación e integración." })}
            <a class="text-link mt-8 inline-flex" href="/#contacto" data-route>Conversemos sobre tu operación <span aria-hidden="true">→</span></a>
          </div>
          <div class="needs-list">${needs.map(([number, title, description]) => `<article><span>${number}</span><div><h3>${title}</h3><p>${description}</p></div></article>`).join("")}</div>
        </div>
      </section>

      <section class="section-pad why-section" aria-labelledby="why-title">
        <div class="site-container">
          ${SectionHeader({ eyebrow: "Por qué GR SoftMedia", title: "Tecnología que entiende la operación y permanece cerca.", description: "Construimos productos con visión de largo plazo y los adaptamos a la realidad de cada implementación." })}
          <div class="reason-grid">${reasons.map(([title, description], index) => `<article><span>0${index + 1}</span><h3>${title}</h3><p>${description}</p></article>`).join("")}</div>
        </div>
      </section>

      <section id="proceso" class="section-pad bg-white scroll-mt-24" aria-labelledby="process-title">
        <div class="site-container">
          ${SectionHeader({ eyebrow: "Cómo trabajamos", title: "Una implementación clara, de principio a evolución.", description: "Un proceso breve para pasar del diagnóstico a una solución adoptada por tu equipo." })}
          ${ProcessSteps()}
        </div>
      </section>

      <section class="section-pad services-section" aria-labelledby="services-title">
        <div class="site-container">
          <div class="section-heading-row">
            ${SectionHeader({ eyebrow: "Servicios complementarios", title: "El producto es la base. El acompañamiento completa la solución.", description: "Sumamos las capacidades necesarias para integrar, adaptar y sostener cada implementación." })}
            <span class="section-index">Después del producto</span>
          </div>
          <div class="service-grid">${services.map(([title, description], index) => `<article><span>${String(index + 1).padStart(2, "0")}</span><h3>${title}</h3><p>${description}</p></article>`).join("")}</div>
        </div>
      </section>

      <section id="nosotros" class="section-pad about-section scroll-mt-24" aria-labelledby="about-title">
        <div class="site-container about-grid">
          <div>
            <span class="eyebrow eyebrow-light">Nosotros</span>
            <h2 id="about-title">Una empresa de tecnología en crecimiento, construyendo productos para crecer junto a sus clientes.</h2>
          </div>
          <div class="about-copy">
            <p>GR SoftMedia desarrolla productos propios y soluciones conectadas para empresas que necesitan más control sobre su operación.</p>
            <p>Combinamos conocimiento técnico, entendimiento de procesos empresariales y experiencia de implementación para construir relaciones de largo plazo, desde la primera puesta en marcha hasta la evolución continua.</p>
            <div class="about-pillars"><span>Productos propios</span><span>Procesos empresariales</span><span>Relaciones de largo plazo</span></div>
          </div>
        </div>
      </section>

      <section id="contacto" class="section-pad contact-section scroll-mt-24" aria-labelledby="contact-title">
        <div class="site-container contact-grid">
          <div class="contact-copy">
            <span class="eyebrow">Contacto</span>
            <h2 id="contact-title">Hablemos sobre lo que necesita tu empresa.</h2>
            <p>Cuéntanos qué proceso quieres organizar, integrar o automatizar. Te ayudaremos a identificar un punto de partida claro.</p>
            <div class="contact-direct">
              <a href="https://wa.me/18495925424" target="_blank" rel="noopener noreferrer"><span>WhatsApp</span><strong>+1 849-592-5424</strong></a>
              <a href="mailto:contacto@grsoftmedia.com.do"><span>Correo</span><strong>contacto@grsoftmedia.com.do</strong></a>
            </div>
          </div>
          ${ContactForm()}
        </div>
      </section>
    </main>
  `;
}
